import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

export const FLYER_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const flyerError = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (Array.isArray(data.detail)) return data.detail.map((item) => `${item.loc?.slice(1).join(' ')}: ${item.msg}`).join('; ');
  return data.detail || 'The flyer could not be processed. Please try again.';
};

export const useServiceFlyer = (slug) => {
  const password = sessionStorage.getItem('admin_password');
  const auth = useMemo(() => ({ Authorization: `Basic ${btoa(':' + password)}` }), [password]);
  const [content, setContent] = useState(null);
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const dirty = content != null && JSON.stringify(content) !== saved;
  const endpoint = `${FLYER_API}/admin/service-flyers/${slug}`;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setContent(null); setError('');
    fetch(endpoint, { headers: auth, signal: controller.signal, cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error(await flyerError(response));
        return response.json();
      }).then((data) => {
        if (!controller.signal.aborted) { setContent(data.content); setSaved(JSON.stringify(data.content)); }
      }).catch((err) => { if (err.name !== 'AbortError') setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [endpoint, auth]);

  useEffect(() => {
    const warn = (event) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const perform = async (action) => {
    if (lock.current) return;
    if (action === 'reset' && !window.confirm('Restore the original wording for this flyer? Current changes will be replaced.')) return;
    lock.current = true; setBusy(action); setError('');
    try {
      const isDownload = action === 'pdf' || action === 'png';
      const path = action === 'reset' ? '/reset' : isDownload ? `/export?kind=${action}` : '';
      const response = await fetch(endpoint + path, {
        method: action === 'save' ? 'PUT' : 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        ...(action !== 'reset' ? { body: JSON.stringify(content) } : {}),
      });
      if (!response.ok) throw new Error(await flyerError(response));
      if (isDownload) {
        const url = URL.createObjectURL(await response.blob());
        const anchor = document.createElement('a'); anchor.href = url;
        anchor.download = `Pawsome-${slug === 'welcome' ? 'Welcome' : 'Concierge'}-Flyer.${action}`;
        anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        const data = await response.json();
        setContent(data.content); setSaved(JSON.stringify(data.content));
        toast.success(action === 'reset' ? 'Original wording restored' : 'Flyer saved');
      }
    } catch (err) { setError(err.message); toast.error('Flyer needs attention'); }
    finally { lock.current = false; setBusy(''); }
  };

  return { content, setContent, auth, loading, error, busy, dirty, perform, password };
};